import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react";
import BrandLogo from "@/components/common/BrandLogo";
import { FooterSocialIcons } from "@/components/landing/FooterSocials";
import FooterDisclaimer from "@/components/landing/FooterDisclaimer";
import Reveal from "../Reveal";

const IMPORTANT_LINKS = [
  { label: "Blog", to: "/blog" },
  { label: "About Us", to: "/about" },
  { label: "Download App", to: "/download" },
  { label: "Contact Us", to: "/support" },
  { label: "Trading", to: "/trade/spot/BTC-USDT" },
  { label: "Market", to: "/markets" },
  { label: "Fees", to: "/fees" },
];

function FooterHeading({ children }) {
  return (
    <h4 className="text-[13px] font-bold uppercase tracking-[0.14em] text-cm-navy mb-4">
      {children}
    </h4>
  );
}

export default function Footer() {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const onNewsletter = (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubscribed(true);
    setEmail("");
    setTimeout(() => setSubscribed(false), 4000);
  };

  return (
    <footer
      className="pt-16 pb-8"
      style={{ background: "linear-gradient(180deg, #F3F0F8 0%, #EBE7F4 100%)" }}
      data-testid="site-footer"
    >
      <div className="cm-container max-w-[1180px]">
        {/* Main panel */}
        <Reveal variant="fade-up">
          <div
            className="rounded-[20px] border bg-white/80 backdrop-blur-sm px-6 py-10 sm:px-10 sm:py-12"
            style={{ borderColor: "#E2DEFA", boxShadow: "0 24px 60px -40px rgba(29,45,63,0.18)" }}
            data-testid="footer-main-panel"
          >
            <div className="flex flex-col lg:flex-row lg:items-start gap-10 lg:gap-14">
              {/* Brand */}
              <div className="lg:w-[260px] shrink-0" data-testid="footer-brand-col">
                <BrandLogo size="lg" testId="footer-logo" />
                <p className="mt-5 text-[14px] text-cm-grey leading-[1.75]">
                  Indian crypto exchange for spot and OTC — built with compliance,
                  deep liquidity, and professional tools.
                </p>
              </div>

              {/* Links + newsletter + social — newsletter centered between columns */}
              <div
                className="flex-1 flex flex-col sm:flex-row sm:items-start gap-8 sm:gap-4 lg:gap-6"
                data-testid="footer-main-grid"
              >
                <div className="sm:shrink-0">
                  <FooterHeading>Important links</FooterHeading>
                  <ul className="space-y-2">
                    {IMPORTANT_LINKS.map((item) => (
                      <li key={item.label}>
                        <Link
                          to={item.to}
                          data-testid={`footer-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                          className="text-[14px] text-cm-grey hover:text-cm-blue transition-colors"
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="sm:flex-1 sm:flex sm:justify-center sm:px-4 lg:px-8 min-w-0">
                  <div className="w-full max-w-[280px]">
                    <FooterHeading>Newsletter</FooterHeading>
                    <p className="text-[13px] text-cm-grey leading-relaxed mb-4">
                      Product updates and promotions — no spam.
                    </p>
                    <form onSubmit={onNewsletter} className="space-y-2.5">
                      <div className="flex rounded-[10px] border border-[#D8D6E5] bg-white overflow-hidden focus-within:border-cm-blue transition-colors">
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Your email"
                          data-testid="newsletter-email"
                          className="flex-1 min-w-0 px-3.5 py-2.5 text-[14px] outline-none bg-transparent text-cm-navy placeholder:text-cm-grey"
                        />
                        <button
                          type="submit"
                          data-testid="newsletter-submit"
                          className="shrink-0 px-3.5 bg-cm-blue text-white hover:bg-cm-blue-deep transition-colors flex items-center justify-center"
                          aria-label="Subscribe"
                        >
                          <ArrowRight size={18} />
                        </button>
                      </div>
                      {subscribed ? (
                        <p className="text-[12px] font-medium text-emerald-600" data-testid="newsletter-success">
                          Thanks — you&apos;re subscribed.
                        </p>
                      ) : null}
                    </form>
                  </div>
                </div>

                <div className="sm:shrink-0 sm:ml-auto">
                  <FooterHeading>Follow us</FooterHeading>
                  <p className="text-[13px] text-cm-grey leading-relaxed mb-4">
                    Stay connected on social media.
                  </p>
                  <FooterSocialIcons />
                </div>
              </div>
            </div>
          </div>
        </Reveal>

        {/* Contact strip */}
        <div
          className="mt-10 grid sm:grid-cols-2 gap-8 px-1"
          data-testid="footer-contact"
        >
          <div className="flex gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white border border-[#E2DEFA] text-cm-blue">
              <Phone size={16} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-cm-blue mb-1.5">
                Helpdesk
              </div>
              <a
                href="mailto:helpdesk@coinmbx.com"
                data-testid="footer-helpdesk-email"
                className="flex items-center gap-1.5 text-[14px] text-cm-navy font-semibold hover:text-cm-blue transition"
              >
                <Mail size={14} className="text-cm-grey" />
                helpdesk@coinmbx.com
              </a>
              <a
                href="tel:+918796101200"
                data-testid="footer-helpdesk-phone"
                className="block mt-1 text-[13px] text-cm-grey hover:text-cm-blue transition"
              >
                +91 87961 01200
              </a>
            </div>
          </div>

          <div className="flex gap-3">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white border border-[#E2DEFA] text-cm-blue">
              <MapPin size={16} />
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-cm-blue mb-1.5">
                Registered office
              </div>
              <div className="text-[13px] text-cm-grey leading-[1.7]" data-testid="footer-address">
                <span className="text-cm-navy font-semibold">
                  Megabyte Digispace Technologies Private Limited
                </span>
                <br />
                GD-ITL Northex Towers, Unit 201, 203 &amp; 204, 2nd Floor,
                Pitampura, Netaji Subhash Place,
                North West Delhi, Delhi 110034, India.
              </div>
            </div>
          </div>
        </div>

        <FooterDisclaimer />

        <div
          className="mt-8 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-4 text-[13px] text-cm-grey"
          style={{ borderColor: "#E2DEFA" }}
        >
          <div>© {new Date().getFullYear()} CoinMBX. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <Link to="/privacy" data-testid="footer-link-privacy" className="hover:text-cm-blue transition">
              Privacy Policy
            </Link>
            <Link to="/terms" data-testid="footer-link-terms" className="hover:text-cm-blue transition">
              Terms &amp; Conditions
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
